import { ITranscriptionService } from "./interfaces/ITranscriptionService";
import { OracleTranscriptionAdapter } from "./adapters/OracleTranscriptionAdapter";
import { Providers } from "../../SofyaTranscriber";
import { WhisperTranscriptionAdapter } from "./adapters/WhisperTranscriptionAdapter";
import { WebSocketSecurityValidator } from "../../utils/websocketSecurity";
import { WebSocketConnectionOptions } from "../../ITranscriptionService";
// import { WhisperVadTranscriptionAdapter } from "./adapters/WhisperVadTranscriptionAdapter";

const languageSelector = (language: string) => {
  switch (language) {
    case 'pt-BR':
      return "portuguese"
    case 'en-US':
      return "english"
    case 'es-ES':
      return "spanish"
    default: 
      return "multilingual"
  }
}
export class TranscriptionServiceFactory {
  static async create(provider: Providers, config: any): Promise<ITranscriptionService> {
    switch (provider) {
      case "sofya_compliance":
      case "oracle":
        return new OracleTranscriptionAdapter(config);
      case "sofya_as_service":
      case "stt_wvad":
      case "sofya_whisper_flow":
        if (config.endpoint.endpoint) {
          const websocketOptions = config.websocketOptions;
          const finalUrl = this.buildWebSocketUrl(
            config.endpoint.endpoint,
            config.language,
            config.translation_lang,
            websocketOptions
          );

          const ws = await this.connectToWebSocket(
            finalUrl,
            websocketOptions?.protocols
          );

          // if (provider === "stt_wvad") {
          return new WhisperTranscriptionAdapter({ ...config, ws });
          // }

          // return new WhisperVadTranscriptionAdapter({ ...config, ws });
        } else {
          throw new Error("Endpoint missing for Whisper provider.");
        }
      default:
        throw new Error("Provider não suportado");
    }
  }

  /**
   * Builds the WebSocket URL with validated query parameters
   * @param endpoint - Base WebSocket endpoint
   * @param language - Transcription language
   * @param translationLang - Translation language (optional)
   * @param websocketOptions - Custom WebSocket options (optional)
   * @returns Final validated URL
   */
  private static buildWebSocketUrl(
    endpoint: string,
    language?: string,
    translationLang?: string,
    websocketOptions?: WebSocketConnectionOptions
  ): string {
    const url = new URL(endpoint);
    const validator = new WebSocketSecurityValidator();

    // Add internal params first (these always take precedence)
    if (language) {
      url.searchParams.set('transcription_language', languageSelector(language));
    }
    if (translationLang) {
      url.searchParams.set('translation_language', languageSelector(translationLang));
    }

    // Add custom query params if provided
    if (websocketOptions?.queryParams) {
      const validatedParams = validator.validateQueryParams(websocketOptions.queryParams);

      for (const [key, value] of Object.entries(validatedParams)) {
        url.searchParams.set(key, value);
      }
    }

    const finalUrl = url.toString();

    // Check final URL length
    validator.checkUrlLength(finalUrl);

    return finalUrl;
  }

  /**
   * Connects to a WebSocket endpoint with optional protocols
   * @param endpoint - WebSocket URL
   * @param protocols - Optional WebSocket protocols
   * @returns Promise that resolves to connected WebSocket
   */
  private static async connectToWebSocket(
    endpoint: string,
    protocols?: string | string[]
  ): Promise<WebSocket> {
    return new Promise<WebSocket>((resolve, reject) => {
      const ws = protocols
        ? new WebSocket(endpoint, protocols)
        : new WebSocket(endpoint);

      ws.onopen = () => resolve(ws);
      ws.onerror = (error) => reject(new Error(`WebSocket connection failed: ${endpoint}`));
    });
  }
}
